import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  SFDatagrid,
  SFActionBar,
  SFPageContainer,
  ACTION_BAR_ACTION_TYPES,
  ACTION_BAR_ACTION_NAMES,
} from "@sf-core";
import { PATHS } from "../../constants/paths";
import { GridNames } from "../../constants/gridConfig";
import { useSelector } from "react-redux";
import getNavDefs from "../navDefs/getBenchmarkNavDefs";
import { API_PARAM } from "../../constants/apiParams";
import {
  exportBenchmarkSkillsGrid,
  uploadBenchmarkSkills,
} from "../../services/benchmarkSkillsService";
import { Claims } from "../../constants/claims";

const BenchmarkSkills = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navDefs = getNavDefs({ t });

  const skillsGridLayout = useSelector((state) =>
    state.gridLayout.find(
      (grid) => grid.gridName === GridNames.BENCHMARK_SKILLS
    )
  );

  const handleSkillsDownload = () => {
    exportBenchmarkSkillsGrid(skillsGridLayout?.filterModel ?? {});
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: t("benchmark.skillPathsGrid.rowNumber"),
        field: "rowNumber",
        hide: false,
      },
      {
        headerName: t("benchmark.skillPathsGrid.skillCode"),
        field: "skillCode",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.skillName"),
        field: "skillName",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.skillCategoryCode"),
        field: "skillCategoryCode",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.skillCategory"),
        field: "skillCategory",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.isTechnical"),
        field: "isTechnical",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.skillType"),
        field: "skillType",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.parentSkillCode"),
        field: "parentSkillCode",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.parentSkillName"),
        field: "parentSkillName",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.overallSkillDefinition"),
        field: "overallSkillDefinition",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.proficiencyLevel.entry"),
        field: "proficiencyLevelEntry",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.proficiencyLevel.intermediate"),
        field: "proficiencyLevelIntermediate",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.proficiencyLevel.advanced"),
        field: "proficiencyLevelAdvanced",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.proficiencyLevel.mastery"),
        field: "proficiencyLevelMastery",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.isActive"),
        field: "status",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.definitionSource"),
        field: "definitionSource",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.WEFLevel"),
        field: "wefLevel",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
      {
        headerName: t("benchmark.skillPathsGrid.WEFName"),
        field: "wefName",
        filter: true,
        filterParams: {
          order: 1,
        },
      },
    ],
    []
  );

  const actionDefs = [
    {
      name: ACTION_BAR_ACTION_NAMES.ADD,
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      permissions: [Claims.AdminBenchmarkSkillsImport],
      onClick: () => navigate(PATHS.BENCHMARK_SKILLS_PATH_ADD),
      buttonProps: {
        label: t("benchmark.addButton.addBenchmarkSkills"),
        variant: "primary",
      },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.DOWNLOAD,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      permissions: [Claims.AdminBenchmarkSkillsExport],
      onClick: handleSkillsDownload,
      iconProps: {
        tooltip: t("benchmark.download.downloadSkills"),
        overflowLabel: t("benchmark.download.downloadSkills"),
      },
    },
    {
      name: "validateSkills",
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      permissions: [Claims.AdminBenchmarkSkillsUpload],
      onClick: uploadBenchmarkSkills,
      buttonProps: {
        label: t("benchmark.validate.skillsMicroSkills"),
        variant: "primary",
        async: true,
      },
      confirmation: true,
      confirmationProps: {
        header: t("benchmark.validateButton.validateHeader"),
        message: t("benchmark.validateButton.validateMessage"),
        primaryLabel: t("benchmark.validateButton.validateConfirm"),
        secondaryLabel: t("benchmark.validateButton.validateCancel"),
      },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.FILTER,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      filterParams: {
        gridName: GridNames.BENCHMARK_SKILLS,
      },
    },
  ];

  return (
    <>
      <SFPageContainer disableGutters>
        <SFActionBar
          titleProps={{
            title: t("benchmark.titles.benchmarkSkills"),
          }}
          actionDefs={actionDefs}
          navDefs={navDefs}
        />
        <SFDatagrid
          canDownload={false}
          columnDefs={columnDefs}
          hasRowSelection={true}
          gridName={GridNames.BENCHMARK_SKILLS}
          datasourceUrl={API_PARAM.BENCHMARK_SKILLS_GRID}
          filterValuesUrl={API_PARAM.BENCHMARK_SKILLS_COL_FILTER}
          basicFiltering
        />
      </SFPageContainer>
    </>
  );
};

export default BenchmarkSkills;
